'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BookingFiltersProps {
  onFilterChange: (filters: FilterState) => void;
}

interface FilterState {
  status: string[];
  dateRange: string;
  sortBy: string;
}

export default function BookingFilters({ onFilterChange }: BookingFiltersProps) {
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    dateRange: 'all',
    sortBy: 'date-desc'
  });

  const statusOptions = [
    { value: 'confirmed', label: 'Confirmed', color: 'text-success' },
    { value: 'pending', label: 'Pending', color: 'text-warning' },
    { value: 'cancelled', label: 'Cancelled', color: 'text-error' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'past', label: 'Past Trips' },
    { value: 'this-year', label: 'This Year' }
  ];

  const sortOptions = [
    { value: 'date-desc', label: 'Newest First' },
    { value: 'date-asc', label: 'Oldest First' },
    { value: 'amount-desc', label: 'Highest Amount' },
    { value: 'amount-asc', label: 'Lowest Amount' }
  ];

  const handleStatusToggle = (status: string) => {
    const newStatus = filters.status.includes(status)
      ? filters.status.filter(s => s !== status)
      : [...filters.status, status];
    
    const newFilters = { ...filters, status: newStatus };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleDateRangeChange = (dateRange: string) => {
    const newFilters = { ...filters, dateRange };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSortChange = (sortBy: string) => {
    const newFilters = { ...filters, sortBy };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  return (
    <div className="bg-card rounded-xl shadow-brand p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-semibold text-lg text-foreground">
          Filter Bookings
        </h3>
        <button
          onClick={() => {
            const resetFilters = { status: [], dateRange: 'all', sortBy: 'date-desc' };
            setFilters(resetFilters);
            onFilterChange(resetFilters);
          }}
          className="text-sm font-medium text-primary hover:text-secondary transition-colors duration-300"
        >
          Reset
        </button>
      </div>

      <div className="space-y-6">
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-3">
            Status
          </label>
          <div className="space-y-2">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleStatusToggle(option.value)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all duration-200 ${
                  filters.status.includes(option.value)
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <span className={`font-medium ${option.color}`}>
                  {option.label}
                </span>
                {filters.status.includes(option.value) && (
                  <Icon name="CheckIcon" size={18} className="text-primary" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Date Range Filter */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-3">
            Date Range
          </label>
          <div className="space-y-2">
            {dateRangeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleDateRangeChange(option.value)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all duration-200 ${
                  filters.dateRange === option.value
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <span className="font-medium text-foreground">
                  {option.label}
                </span>
                {filters.dateRange === option.value && (
                  <Icon name="CheckIcon" size={18} className="text-primary" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Sort By */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-3">
            Sort By
          </label>
          <div className="space-y-2">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSortChange(option.value)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all duration-200 ${
                  filters.sortBy === option.value
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <span className="font-medium text-foreground">
                  {option.label}
                </span>
                {filters.sortBy === option.value && (
                  <Icon name="CheckIcon" size={18} className="text-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}